import { Async } from '../../components/Async.tsx';
import { DataTable, type Column, type TotalsRow } from '../../components/DataTable.tsx';
import { EmptyState, Panel } from '../../components/States.tsx';
import { formatDuration, formatNumber } from '../../lib/format.ts';
import { useLocale, useT } from '../../lib/i18n.tsx';
import { useSellerCalls, type SellerCallsResponse } from '../../lib/queries.ts';
import type { DashboardParams } from '../../lib/url-state.ts';

type Row = SellerCallsResponse['data']['rows'][number];

/** Calls, clients talked to and talk time per salesperson (KPI_DEFINITIONS §6.9). */
export function SellerCallsPanel({ params }: { params: DashboardParams }) {
  const t = useT();
  const locale = useLocale();
  const calls = useSellerCalls(params);
  const units = { minutes: t.units.minutes, hours: t.units.hours };
  const n = (value: number): string => formatNumber(value, locale);
  const talk = (seconds: number): string => formatDuration(seconds / 60, locale, units);
  const avgTalk = (r: { answered: number; talkSeconds: number }): number | null =>
    r.answered === 0 ? null : r.talkSeconds / r.answered;

  return (
    <Panel title={t.team.calls.title}>
      <p className="mb-3 text-sm text-muted-foreground">{t.team.calls.note}</p>
      <Async query={calls}>
        {({ data }) => {
          if (data.rows.length === 0)
            return <EmptyState title={t.common.noData} description={t.team.empty} />;
          const columns: Column<Row>[] = [
            {
              id: 'name',
              header: t.team.columns.name,
              sortValue: (r) => r.salesperson.fullName ?? '',
              cell: (r) => r.salesperson.fullName ?? t.team.unassigned,
            },
            {
              id: 'calls',
              header: t.team.calls.calls,
              align: 'right',
              sortValue: (r) => r.calls,
              cell: (r) => n(r.calls),
            },
            {
              id: 'answered',
              header: t.team.calls.answered,
              align: 'right',
              sortValue: (r) => r.answered,
              cell: (r) => n(r.answered),
            },
            {
              id: 'clients',
              header: t.team.calls.clients,
              align: 'right',
              sortValue: (r) => r.clientsTalked,
              cell: (r) => n(r.clientsTalked),
            },
            {
              id: 'talk',
              header: t.team.calls.talkTime,
              align: 'right',
              sortValue: (r) => r.talkSeconds,
              cell: (r) => talk(r.talkSeconds),
            },
            {
              id: 'avg',
              header: t.team.calls.avgTalk,
              align: 'right',
              sortValue: (r) => avgTalk(r),
              cell: (r) => {
                const avg = avgTalk(r);
                return avg === null ? '—' : formatDuration(avg / 60, locale, units);
              },
            },
          ];
          const totals: TotalsRow = {
            label: t.team.totals,
            cells: {
              calls: n(data.totals.calls),
              answered: n(data.totals.answered),
              clients: n(data.totals.clientsTalked),
              talk: talk(data.totals.talkSeconds),
              avg: (() => {
                const avg = avgTalk(data.totals);
                return avg === null ? '—' : formatDuration(avg / 60, locale, units);
              })(),
            },
          };
          return (
            <>
              <DataTable
                caption={t.team.calls.title}
                columns={columns}
                rows={data.rows}
                rowKey={(r) => r.salesperson.id ?? 'unassigned'}
                defaultSort={{ id: 'talk', direction: 'desc' }}
                totals={totals}
              />
              <p className="mt-2 text-xs text-muted-foreground">{t.team.calls.totalsNote}</p>
            </>
          );
        }}
      </Async>
    </Panel>
  );
}
